// Send Cargo Receipt — courier-handoff flow.
// Mirrors SelfPickup (customer + station → parcel list with the same criteria),
// but the customer is NOT at the branch: one "Send" action (no popup) (1) fires a
// 2-part WhatsApp message in one queue [receipt image(s) → claim terms] — the
// parcel list itself already went out earlier as part of the Cargo Prep
// notification (see backend sendCargoPrepNotification) — then (2) settles the fee
// against the wallet only (lines:[] → wallet-or-approval, same backend as
// SelfPickup's collect) and moves the parcels to END with deliveryMethod="Cargo".
import { useTheme } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import Card from "@mui/material/Card";
import { grey } from "@mui/material/colors";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  PhotoCameraOutlined as PhotoCameraOutlinedIcon,
  CheckRounded as CheckRoundedIcon,
} from "@mui/icons-material";
import moment from "moment";
import Swal from "sweetalert2";

import { GetStationDetailById, GetFinalStationList } from "api/stations";
import { GetCustomersList } from "api/customers";
import { getAllCustomerGroups } from "api/customerGroup";
import {
  GetReadyToPickupPackages,
  cargoPrepItemsPhoto,
  cargoPrepBoxPhoto,
} from "api/packages";
import { GetLatestRate } from "api/currencyExchangeRate";
import { collectShippingPayment } from "api/financial";
import { sendCargoReceipt } from "api/whatsapp";
import useStationRefresh from "hooks/useStationRefresh";
import FlexBetween from "components/FlexBetween";
import PackageEditDialog from "components/PackageEditDialog";
import TrackingDetailDialog from "components/TrackingDetialDialog.";
import { playCorrectSound, playIncorrectSound } from "utils";

const MONO = "'Space Grotesk', ui-monospace, monospace";
const TEXT_BODY = "#2c2937";
const TEXT_FAINT = "#a8a3b4";
const BORDER = "#ece9f1";
const GROUP_BG = "#faf9fc";
const GROUP_BORDER = "#f0eef5";
const CHEVRON = "#b9b4c4";

// Part 2 — claim-protection terms / warning text (sent as its own message).
function buildClaimMessage() {
  return `⚠️ ເພື່ອປົກປ້ອງສິດທິຂອງທ່ານ:
1. ກະລຸນາຖ່າຍວິດີໂອແກະກ່ອງແບບບໍ່ຕັດຕໍ່ (ເພື່ອໃຊ້ເປັນຫຼັກຖານເຄລມສິນຄ້າເສຍຫາຍ ຫຼື ສູນຫາຍ).
2. ຟ້າວຮັບສິນຄ້າທັນທີເມື່ອຮອດສາຂາປາຍທາງ (ເນື່ອງຈາກໄລຍະເວລາປະກັນມີຈຳກັດ).

⚠️To protect your rights:
1. Please film an unedited unboxing video (Required as evidence for any damage or loss claims).
2. Pick up your item as soon as it arrives (Product warranty period is limited).`;
}

const initials = (name) => {
  const p = (name || "?").trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || "?").slice(0, 2)).toUpperCase();
};

// Cargo Prep status derived straight from Package.photos — "none" (not started),
// "waitingBoxPhoto" (items photo saved, box photo still pending — may be minutes
// or a shift later), or "complete" (both saved, WhatsApp notification already sent).
const cargoPrepStatusOf = (pkg) => {
  const cats = new Set((pkg.photos || []).map((p) => p.category));
  if (cats.has("cargo_items") && cats.has("cargo_box")) return "complete";
  if (cats.has("cargo_items")) return "waitingBoxPhoto";
  return "none";
};

function CargoReceiptSender() {
  const theme = useTheme();
  const { t } = useTranslation();
  const cr = "whatsappPage.cargoReceipt";

  const [stationList, setStationList] = useState(null);
  const [selectedStation, setSelectedStation] = useState(null);
  const [rows, setRows] = useState(null);

  const [customer, setCustomer] = useState({});
  const [searchCustomer, setSearchCustomer] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);

  const [attachments, setAttachments] = useState([]);
  const [isSending, setIsSending] = useState(false);

  // "prep" = Cargo Prep (stage 1, photograph the box before drop-off) — "send" =
  // the existing Send Cargo Receipt flow (stage 2, after the courier hands back a
  // receipt). Two separate jobs, often done by different staff at different times
  // of day, kept as tabs on the same customer/station selection.
  const [activeTab, setActiveTab] = useState("prep");
  // Packages currently checked for the NEXT prep photo action (one box's worth).
  const [prepSelected, setPrepSelected] = useState([]);
  const [prepSubmitting, setPrepSubmitting] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [packageData, setPackageData] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [selectedTrackingNumber, setSelectedTrackingNumber] = useState("");

  const [currencyExchangeRate, setCurrencyExchangeRate] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalShippingFee, setTotalShippingFee] = useState(0);
  const [feeLAK, setFeeLAK] = useState(0);
  const [feeUSD, setFeeUSD] = useState(0);

  const fileInputRef = useRef(null);

  const { user: _user, defaultStation: _defaultStation, ready } = useStationRefresh();
  const userStations = Array.isArray(_user?.stations) ? _user.stations : [];
  const isAgentUser = _user?.role === "agent";

  // ── bootstrap: stations, fx rate, customer groups ──────────────────────────
  useEffect(() => {
    GetFinalStationList().then((res) => {
      if (res && res.success) {
        const arr = Array.isArray(res.data) ? res.data : [];
        const validStation = [];
        arr.forEach((finalStation) => {
          userStations.forEach((userStation) => {
            if (finalStation.id === userStation.id) validStation.push(finalStation);
          });
        });
        const availableStations = validStation.length > 0 ? validStation : arr;
        setStationList(availableStations);

        const initialStationId = _defaultStation?.id || availableStations[0]?.id;
        if (initialStationId) {
          GetStationDetailById(initialStationId).then((r) => {
            if (r && r.success) setSelectedStation(r.data);
          });
        }
      }
    });
    GetLatestRate().then((res) => {
      if (res && res.status === 200) setCurrencyExchangeRate(res.data);
    });
    getAllCustomerGroups().then((res) => {
      if (res && res.success) setCustomerGroups(res.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    if (searchCustomer) {
      GetCustomersList(searchCustomer).then((res) => {
        if (res && res.success) setCustomerList(res.data);
      });
    }
  }, [searchCustomer]);

  // ── load the customer's ready parcels at the selected station ──────────────
  // Reloaded from scratch after every Cargo Prep photo save too (not kept in local
  // state across it) — a package's prep status must always reflect the server, since
  // staff may close this page mid-prep and a different staff member could finish it.
  const reloadRows = () => {
    if (!(customer?._id && selectedStation?.id)) return;
    GetReadyToPickupPackages(customer._id, selectedStation.id).then((res) => {
      if (res && res.status === 200) {
        const packages = res.data.packages;
        // Only auto-select parcels that have finished Cargo Prep — the rest may be
        // headed to a different address, or held for later pickup, and shouldn't
        // block or get swept into this courier handoff.
        packages.forEach((p, index) => {
          packages[index].sendStatus = cargoPrepStatusOf(p) === "complete";
        });
        setRows(packages);
        // Auto-select: when every actionable parcel sits in the same single Cargo
        // Prep stage (all "not started" or all "waiting for box photo"), pre-select
        // them all so staff can go straight to the action button. Left empty when
        // the two stages are mixed, since selecting one group over the other would
        // be an arbitrary guess.
        const none = packages.filter((p) => cargoPrepStatusOf(p) === "none");
        const waiting = packages.filter((p) => cargoPrepStatusOf(p) === "waitingBoxPhoto");
        if (none.length > 0 && waiting.length === 0) setPrepSelected(none);
        else if (waiting.length > 0 && none.length === 0) setPrepSelected(waiting);
        else setPrepSelected([]);
      } else {
        Swal.fire({
          title: "API call error",
          text: res?.message || "Failed to load packages",
          icon: "error",
        });
      }
    });
  };

  useEffect(() => {
    reloadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, selectedStation]);

  // ── totals (mirror SelfPickup) ─────────────────────────────────────────────
  useEffect(() => {
    if (rows) {
      const sendItems = rows.filter((item) => item.sendStatus === true);
      setTotalItems(sendItems.length);
      setTotalShippingFee(
        sendItems.reduce((acc, object) => {
          const fee = isAgentUser
            ? parseInt(object.finalShippingFee ?? 0)
            : parseInt(object.shippingFee ?? 0);
          return parseInt(acc) + fee + parseInt(object.amount ?? 0);
        }, 0)
      );
    }
  }, [rows, isAgentUser]);

  useEffect(() => {
    if (totalShippingFee > 0 && currencyExchangeRate) {
      const lak = parseInt(totalShippingFee) * parseInt(currencyExchangeRate.LAK);
      setFeeLAK((Math.round(lak / 1000) * 1000).toLocaleString());
      setFeeUSD((parseInt(totalShippingFee) / parseInt(currencyExchangeRate.USD)).toFixed(2));
    } else {
      setFeeLAK(0);
      setFeeUSD(0);
    }
  }, [totalShippingFee, currencyExchangeRate]);

  const handleSendStatus = (index) => {
    setRows((prev) =>
      prev.map((p, i) =>
        i === index && cargoPrepStatusOf(p) === "complete" ? { ...p, sendStatus: !p.sendStatus } : p
      )
    );
  };

  // Only sweeps in parcels that have finished Cargo Prep — parcels still pending
  // Prep (e.g. bound for a different address, or held for later pickup) are left
  // alone rather than forced into this send.
  const handleMarkAllReady = () => {
    setRows((prev) =>
      Array.isArray(prev)
        ? prev.map((p) => (cargoPrepStatusOf(p) === "complete" ? { ...p, sendStatus: true } : p))
        : prev
    );
  };

  // ── Cargo Prep (stage 1) ────────────────────────────────────────────────────
  const togglePrepSelected = (pkg) => {
    setPrepSelected((prev) =>
      prev.some((p) => p._id === pkg._id) ? prev.filter((p) => p._id !== pkg._id) : [...prev, pkg]
    );
  };

  const submitItemsPhoto = async (file) => {
    if (!file || prepSelected.length === 0) return;
    setPrepSubmitting(true);
    try {
      const res = await cargoPrepItemsPhoto(customer._id, prepSelected.map((p) => p._id), file, selectedStation?.id);
      if (!res || !res.success) throw new Error((res && res.message) || t("common.error"));
      reloadRows();
      playCorrectSound();
    } catch (err) {
      playIncorrectSound();
      Swal.fire({ icon: "error", text: err.message || t("common.error"), toast: true, position: "top-right", timer: 5000, showConfirmButton: true });
    } finally {
      setPrepSubmitting(false);
    }
  };

  const submitBoxPhoto = async (file) => {
    if (!file || prepSelected.length === 0) return;
    setPrepSubmitting(true);
    try {
      const res = await cargoPrepBoxPhoto(prepSelected.map((p) => p._id), file, selectedStation?.id);
      if (!res || !res.success) throw new Error((res && res.message) || t("common.error"));
      reloadRows();
      playCorrectSound();
      Swal.fire({
        icon: "success",
        text: t(`${cr}.prep.completeToast`),
        toast: true,
        position: "top-right",
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      playIncorrectSound();
      Swal.fire({ icon: "error", text: err.message || t("common.error"), toast: true, position: "top-right", timer: 5000, showConfirmButton: true });
    } finally {
      setPrepSubmitting(false);
    }
  };

  const getCustomerGroup = () => {
    if (customer.groupId && customerGroups.length > 0) {
      return customerGroups.find((group) => group.id === customer.groupId) || null;
    }
    return null;
  };

  const buttonEditHandler = (rowValue) => {
    setPackageData(rowValue);
    setEditDialogOpen(true);
  };

  // Same reconcile as SelfPickup: drop parcels that no longer match the list filter.
  const reconcileRowsAfterEdit = () => {
    setRows((prev) =>
      Array.isArray(prev)
        ? prev.filter(
            (p) =>
              p.customer?._id === customer?._id &&
              String(p.station ?? "") === String(selectedStation?.id ?? "") &&
              p.paymentStatus === false &&
              p.whatsappStatus === true
          )
        : prev
    );
  };

  // Parcels switched ON and not already locked by a pending approval.
  const sendablePackages = Array.isArray(rows)
    ? rows.filter((r) => r.sendStatus === true && !r.pendingShippingPaymentId)
    : [];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const handleRemoveAttachment = (id) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.preview) URL.revokeObjectURL(att.preview);
      return prev.filter((a) => a.id !== id);
    });
  };

  // Every sendable parcel must have finished Cargo Prep (stage 1) first — the
  // backend enforces this too (sendCargoReceipt gate), this just avoids a round
  // trip and tells staff which parcels are still blocking the send.
  const notPrepped = sendablePackages.filter((p) => cargoPrepStatusOf(p) !== "complete");
  // While any loaded parcel is still pending Cargo Prep, hide the package list
  // entirely instead of showing a mixed ready/not-ready grid — staff finish
  // Prep first, then come back to see a clean, fully-sendable list.
  const pendingPrepRows = Array.isArray(rows)
    ? rows.filter((p) => cargoPrepStatusOf(p) !== "complete")
    : [];
  const canSend =
    !!customer?._id &&
    !!selectedStation?.id &&
    attachments.length > 0 &&
    sendablePackages.length > 0 &&
    notPrepped.length === 0 &&
    !isSending;

  // ── single send action: WhatsApp (2 parts) → wallet settlement ─────────────
  const handleSend = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      // 1) Queue the 2-part WhatsApp message (receipt image → claim terms). The
      // parcel list already went out with the Cargo Prep notification.
      const waRes = await sendCargoReceipt({
        customer: {
          _id: customer._id,
          whatsapp: customer.whatsapp,
          name: customer.name,
          groupId: customer.groupId,
        },
        packageIds: sendablePackages.map((p) => p._id),
        claimMessage: buildClaimMessage(),
        attachments,
      });
      if (!waRes.success) throw new Error(waRes.message || t("common.error"));

      // 2) Settle against the wallet only (no cash/bank lines) + stamp Cargo.
      const fd = new FormData();
      fd.append("customerId", customer._id);
      fd.append("stationId", selectedStation.id);
      fd.append("packageIds", JSON.stringify(sendablePackages.map((p) => p._id)));
      fd.append("lines", JSON.stringify([]));
      fd.append("deliveryMethod", "Cargo");
      const payRes = await collectShippingPayment(fd);
      if (!payRes || !payRes.success) {
        throw new Error((payRes && payRes.message) || t("common.error"));
      }

      // success — drop the settled parcels + reset attachments.
      const sentIds = new Set(sendablePackages.map((p) => p._id));
      setRows((prev) =>
        Array.isArray(prev) ? prev.filter((r) => !sentIds.has(r._id)) : prev
      );
      attachments.forEach((a) => {
        if (a.preview) URL.revokeObjectURL(a.preview);
      });
      setAttachments([]);
      playCorrectSound();
      Swal.fire({
        icon: "success",
        text:
          payRes.status === "pending_approval"
            ? t("pickup.payment.submittedForApproval")
            : t("pickup.payment.collected"),
        toast: true,
        position: "top-right",
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      playIncorrectSound();
      Swal.fire({
        icon: "error",
        text: err.message || t("common.error"),
        toast: true,
        position: "top-right",
        timer: 5000,
        showConfirmButton: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  // ── design tokens (match SelfPickup) ───────────────────────────────────────
  const accent = theme.palette.secondary.main;
  const accentLight = theme.palette.secondary.light;
  const accentDark = theme.palette.secondary.dark;
  const tintBg = alpha(accent, 0.06);
  const PANEL_RADIUS = "20px";
  const KPI_RADIUS = "18px";
  const CARD_RADIUS = "20px";
  const softShadow =
    "0 1px 2px rgba(20,18,30,.05), 0 10px 28px -20px rgba(20,18,30,.22)";
  const cardShadow =
    "0 1px 2px rgba(20,18,30,.05), 0 12px 28px -18px rgba(20,18,30,.24)";
  const iconBtnSx = {
    width: 32,
    height: 32,
    border: `1px solid ${BORDER}`,
    borderRadius: "9px",
    color: accent,
    "&:hover": { background: tintBg, borderColor: alpha(accent, 0.45) },
  };
  const readyCount = Array.isArray(rows)
    ? rows.filter((r) => r.sendStatus === true).length
    : 0;
  const totalCount = Array.isArray(rows) ? rows.length : 0;
  const customerSelected = !!customer?._id;

  return (
    <Box>
      {/* stage switcher — Cargo Prep (stage 1, before courier drop-off) vs Send
          Cargo Receipt (stage 2, after the courier hands back a receipt) */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 2, minHeight: 0, "& .MuiTab-root": { minHeight: 0, py: 1.25, fontWeight: 700, textTransform: "none" } }}
      >
        <Tab value="prep" label={t(`${cr}.prep.tabLabel`)} />
        <Tab value="send" label={t(`${cr}.sendButton`)} />
      </Tabs>

      {activeTab === "send" && (
        <>
      {/* attachments (cargo receipt) */}
      <Paper
        elevation={0}
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: PANEL_RADIUS,
          p: { xs: 2, md: "20px 24px" },
          boxShadow: softShadow,
          mb: 2,
        }}
      >
        <FlexBetween sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
            }}
            color="text.secondary"
          >
            {t(`${cr}.attachmentSection`)}
          </Typography>
          {attachments.length > 0 && (
            <Chip
              size="small"
              color="secondary"
              label={`${attachments.length}`}
              icon={<ImageIcon />}
            />
          )}
        </FlexBetween>

        <Button
          variant="outlined"
          color="secondary"
          size="small"
          startIcon={<AttachFileIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
          sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none" }}
        >
          {t(`${cr}.selectFiles`)}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {attachments.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1.5 }}
          >
            {t(`${cr}.noAttachment`)}
          </Typography>
        ) : (
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gap: 1,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fill, minmax(180px, 1fr))",
              },
            }}
          >
            {attachments.map((att) => (
              <Box
                key={att.id}
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "1 / 1",
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  overflow: "hidden",
                }}
              >
                {att.preview ? (
                  <Box
                    component="img"
                    src={att.preview}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      p: 1,
                    }}
                  >
                    <AttachFileIcon color="secondary" />
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ maxWidth: "100%", textAlign: "center" }}
                    >
                      {att.name}
                    </Typography>
                  </Box>
                )}
                <IconButton
                  size="small"
                  onClick={() => handleRemoveAttachment(att.id)}
                  sx={{
                    position: "absolute",
                    top: 1,
                    right: 1,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    padding: "2px",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
        </>
      )}

      {/* filter panel: customer + station + send — shared by both tabs */}
      <Paper
        elevation={0}
        sx={{
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: PANEL_RADIUS,
          p: { xs: 2, md: "20px 24px" },
          boxShadow: softShadow,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          {/* customer */}
          <Box sx={{ flex: 1, minWidth: { md: 260 } }}>
            <Autocomplete
              onInput={(e) => setSearchCustomer(e.target.value)}
              onChange={(e, value) => {
                if (value) {
                  setCustomer({
                    _id: value._id,
                    id: value.id,
                    name: value.name,
                    whatsapp: value.whatsapp,
                    groupId: value.groupId,
                    pr_id: value.province?.pr_id,
                  });
                } else {
                  setCustomer({});
                  setRows(null);
                }
              }}
              disablePortal
              id="combo-box-customer"
              options={customerList}
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              getOptionLabel={(option) =>
                option.name + " - " + option.whatsapp || ""
              }
              popupIcon={<ExpandMoreIcon sx={{ color: CHEVRON }} />}
              sx={{ "& .MuiAutocomplete-clearIndicator": { color: CHEVRON } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t("pickup.selfPickup.customer")}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    sx: {
                      fontSize: "10.5px",
                      letterSpacing: ".06em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: TEXT_FAINT,
                      background: theme.palette.background.paper,
                      px: 0.5,
                      "&.Mui-focused": { color: TEXT_FAINT },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: customerSelected ? (
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
                          color: "#fff",
                          fontFamily: MONO,
                          fontWeight: 700,
                          fontSize: 11,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flex: "none",
                          mr: 1,
                        }}
                      >
                        {initials(customer.name)}
                      </Box>
                    ) : null,
                    sx: {
                      height: 46,
                      borderRadius: "12px",
                      background: customerSelected
                        ? tintBg
                        : theme.palette.background.paper,
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: TEXT_BODY,
                      "& fieldset": {
                        borderColor: customerSelected
                          ? alpha(accent, 0.35)
                          : BORDER,
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": { borderColor: alpha(accent, 0.35) },
                      "&.Mui-focused fieldset": { borderColor: alpha(accent, 0.5) },
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* station */}
          <FormControl sx={{ minWidth: { md: 200 } }} fullWidth>
            <InputLabel
              sx={{
                fontSize: "10.5px",
                letterSpacing: ".06em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: TEXT_FAINT,
                background: theme.palette.background.paper,
                px: 0.5,
                "&.Mui-focused": { color: TEXT_FAINT },
              }}
            >
              {t("pickup.selfPickup.selectStation")}
            </InputLabel>
            <Select
              label={t("pickup.selfPickup.selectStation")}
              value={selectedStation ? selectedStation.id : ""}
              onChange={(e) => {
                GetStationDetailById(e.target.value).then((res) => {
                  if (res.success) setSelectedStation(res.data);
                });
              }}
              IconComponent={(props) => (
                <ExpandMoreIcon {...props} sx={{ color: CHEVRON }} />
              )}
              required
              sx={{
                height: 46,
                borderRadius: "12px",
                fontSize: 13.5,
                fontWeight: 600,
                color: TEXT_BODY,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: BORDER,
                  borderWidth: "1.5px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(accent, 0.35),
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(accent, 0.5),
                },
              }}
            >
              {stationList &&
                stationList.map(({ id, name }) => (
                  <MenuItem key={id} value={id}>
                    {id} - {name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* send — only on the Send Receipt tab */}
          {activeTab === "send" && (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={
                  isSending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )
                }
                onClick={handleSend}
                disabled={!canSend}
                sx={{
                  height: 46,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: 13,
                  px: 3,
                  flex: { xs: 1, md: "none" },
                  background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
                  boxShadow: `0 4px 14px -4px ${alpha(accent, 0.6)}`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
                    opacity: 0.92,
                  },
                }}
              >
                {isSending ? t(`${cr}.sending`) : t(`${cr}.sendButton`)}
              </Button>
            </Stack>
          )}
        </Stack>

        {/* customer group info */}
        {customer._id &&
          (() => {
            const customerGroup = getCustomerGroup();
            const status = customerGroup?.whatsappStatus?.status;
            return (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{
                  mt: 1.75,
                  px: 1.75,
                  py: 1.25,
                  background: GROUP_BG,
                  border: `1px solid ${GROUP_BORDER}`,
                  borderRadius: "11px",
                }}
              >
                <Typography sx={{ fontSize: 12.5, color: "#7c7787" }}>
                  {t("pickup.selfPickup.customerGroup")}:{" "}
                  <Box component="strong" sx={{ color: TEXT_BODY, fontWeight: 600 }}>
                    {customerGroups.length === 0
                      ? t("pickup.selfPickup.loadingGroups")
                      : customerGroup?.name ||
                        `${t("pickup.selfPickup.noGroup")} (ID: ${
                          customer.groupId || "none"
                        })`}
                  </Box>
                </Typography>
                {status && (
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: "#fff",
                      background:
                        status === "ready"
                          ? "linear-gradient(135deg,#16a34a,#15803d)"
                          : "linear-gradient(135deg,#9ca3af,#6b7280)",
                      px: 1.25,
                      py: 0.25,
                      borderRadius: "999px",
                    }}
                  >
                    {status}
                  </Box>
                )}
              </Stack>
            );
          })()}
      </Paper>

      {activeTab === "prep" && (
        <CargoPrepPanel
          t={t}
          cr={cr}
          theme={theme}
          rows={rows}
          prepSelected={prepSelected}
          togglePrepSelected={togglePrepSelected}
          setPrepSelected={setPrepSelected}
          submitItemsPhoto={submitItemsPhoto}
          submitBoxPhoto={submitBoxPhoto}
          prepSubmitting={prepSubmitting}
          onTrackingClick={(tn) => { setSelectedTrackingNumber(tn); setTrackingDialogOpen(true); }}
        />
      )}

      {/* Informational only — parcels still pending Cargo Prep stay in the grid
          below as locked/unsendable, they no longer block sending the rest. */}
      {activeTab === "send" && pendingPrepRows.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: "14px",
            border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
            background: alpha(theme.palette.warning.main, 0.07),
          }}
        >
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }} color="warning.main">
            {t(`${cr}.prep.notPreppedWarning`, { list: pendingPrepRows.map((p) => p.tracking).join(", ") })}
          </Typography>
          <Link
            component="button"
            type="button"
            color="secondary"
            underline="hover"
            onClick={() => setActiveTab("prep")}
            sx={{ display: "inline-block", mt: 0.5, fontSize: 12, fontWeight: 700 }}
          >
            {t(`${cr}.prep.tabLabel`)}
          </Link>
        </Box>
      )}

      {activeTab === "send" && (
        <>
      {/* summary KPI strip */}
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gap: 1.75,
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}
      >
        {[
          {
            label: t("pickup.selfPickup.totalItems"),
            value: totalItems,
            hint: t("pickup.selfPickup.readyToCollect"),
            big: true,
          },
          {
            label: t("pickup.selfPickup.feeThb"),
            value: `฿${Number(totalShippingFee || 0).toLocaleString()}`,
            hint: t("pickup.selfPickup.totalPayable"),
            highlight: true,
            accentValue: true,
          },
          {
            label: t("pickup.selfPickup.feeLak"),
            value: `₭${feeLAK || 0}`,
            hint: t("pickup.selfPickup.totalPayable"),
          },
          {
            label: t("pickup.selfPickup.feeUsd"),
            value: `$${feeUSD || 0}`,
            hint: t("pickup.selfPickup.totalPayable"),
          },
        ].map((kpi, i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{
              borderRadius: KPI_RADIUS,
              p: "16px 20px",
              border: `1px solid ${
                kpi.highlight ? alpha(accent, 0.25) : theme.palette.divider
              }`,
              background: kpi.highlight
                ? `linear-gradient(155deg, ${theme.palette.background.paper} 0%, ${alpha(
                    accent,
                    0.09
                  )} 100%)`
                : theme.palette.background.paper,
              boxShadow: softShadow,
            }}
          >
            <Typography
              sx={{
                fontSize: 10.5,
                letterSpacing: ".14em",
                fontWeight: 700,
                textTransform: "uppercase",
              }}
              color="text.secondary"
            >
              {kpi.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: MONO,
                fontWeight: 700,
                fontSize: kpi.big ? 30 : 24,
                mt: 0.5,
                lineHeight: 1.1,
              }}
              color={kpi.accentValue ? "secondary.main" : "text.primary"}
            >
              {kpi.value}
            </Typography>
            <Typography sx={{ fontSize: 11.5, mt: 0.25 }} color="text.secondary">
              {kpi.hint}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* section header */}
      <FlexBetween sx={{ mt: 3.5, mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: ".1em",
              textTransform: "uppercase",
            }}
            color="text.secondary"
          >
            {t("pickup.selfPickup.packages")}
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.5 }} color="text.secondary">
            {t("pickup.selfPickup.markedReady", {
              ready: readyCount,
              total: totalCount,
            })}
          </Typography>
        </Box>
        {totalCount > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleMarkAllReady}
            sx={{ borderRadius: "9px", fontWeight: 700, textTransform: "none" }}
          >
            {t("pickup.selfPickup.markAllReady")}
          </Button>
        )}
      </FlexBetween>

      {/* package grid */}
      <Grid container spacing={2.25}>
        {rows &&
          rows.map((packageItem, index) => {
            const isSubCustomer = packageItem.isSubCustomer;
            const finalShippingFee = parseInt(packageItem.finalShippingFee ?? 0);
            const shippingFee = parseInt(packageItem.shippingFee ?? 0);
            const amount = parseInt(packageItem.amount ?? 0);
            const totalFee = isSubCustomer
              ? amount + finalShippingFee
              : amount + shippingFee;
            const isPrepped = cargoPrepStatusOf(packageItem) === "complete";
            const ready = packageItem.sendStatus && isPrepped;
            const badgeBg =
              totalFee > 100
                ? `linear-gradient(135deg, ${accent}, ${accentDark})`
                : totalFee > 0
                ? `linear-gradient(135deg, ${alpha(accent, 0.82)}, ${accent})`
                : grey[400];

            return (
              <Grid item key={index} xl={6} md={6} xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    background: theme.palette.background.paper,
                    border: `1px solid ${
                      ready ? alpha(accent, 0.35) : theme.palette.divider
                    }`,
                    borderRadius: CARD_RADIUS,
                    boxShadow: cardShadow,
                    transition: "border-color .2s, box-shadow .2s",
                    "&:hover": {
                      borderColor: alpha(accent, 0.45),
                      boxShadow: `0 1px 2px rgba(20,18,30,.05), 0 18px 36px -18px ${alpha(
                        accent,
                        0.28
                      )}`,
                    },
                  }}
                >
                  <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", flexGrow: 1 }}>
                    {/* header row */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1.5}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          variant="rounded"
                          aria-label="shipping fee"
                          sx={{
                            width: 46,
                            height: 46,
                            borderRadius: "14px",
                            background: badgeBg,
                            color: "#fff",
                            fontFamily: MONO,
                            fontWeight: 700,
                            fontSize: String(totalFee).length > 3 ? 13 : 16,
                          }}
                        >
                          {totalFee}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Link
                            component="button"
                            type="button"
                            color="secondary"
                            underline="hover"
                            onClick={() => {
                              setSelectedTrackingNumber(packageItem.tracking);
                              setTrackingDialogOpen(true);
                            }}
                            sx={{
                              display: "block",
                              fontFamily: MONO,
                              fontWeight: 700,
                              fontSize: 14.5,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                              textAlign: "left",
                            }}
                          >
                            {packageItem.tracking}
                          </Link>
                          <Typography
                            sx={{ fontSize: 11.5, mt: 0.25, fontFamily: MONO }}
                            color="text.secondary"
                          >
                            {moment(packageItem.updatedAt)
                              .tz("Asia/Vientiane")
                              .format("DD/MM/YYYY, hh:mm:ss")}
                          </Typography>
                        </Box>
                      </Stack>
                      {/* toggle — locked until this parcel finishes Cargo Prep;
                          other parcels for the same customer send independently */}
                      <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                        <Switch
                          color="secondary"
                          checked={ready}
                          disabled={!isPrepped}
                          onChange={() => handleSendStatus(index)}
                        />
                        <Typography
                          sx={{ fontSize: 10.5, fontWeight: 700 }}
                          color={
                            !isPrepped
                              ? "warning.main"
                              : ready
                              ? "secondary.main"
                              : "text.secondary"
                          }
                        >
                          {!isPrepped
                            ? t(`${cr}.prep.needsPrepBadge`)
                            : ready
                            ? t("pickup.selfPickup.ready")
                            : t("pickup.selfPickup.notReady")}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* order id */}
                    <Box
                      sx={{
                        mt: 1.75,
                        px: 1.5,
                        py: 1.25,
                        background: tintBg,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "11px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: ".06em",
                          textTransform: "uppercase",
                          mb: 0.5,
                        }}
                        color="text.secondary"
                      >
                        {t("pickup.selfPickup.orderId")}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: MONO,
                          fontSize: 12.5,
                          fontWeight: 600,
                          wordBreak: "break-all",
                        }}
                        color="secondary.main"
                      >
                        {packageItem.orderId
                          ? packageItem.orderId
                          : t("pickup.selfPickup.noOrderId")}
                      </Typography>
                    </Box>

                    {/* description */}
                    {packageItem.description && (
                      <Typography
                        sx={{
                          mt: 1.25,
                          fontSize: 12.5,
                          lineHeight: 1.55,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        color="text.secondary"
                      >
                        {packageItem.description}
                      </Typography>
                    )}

                    {/* remark */}
                    {packageItem.remark && (
                      <Typography sx={{ mt: 1, fontSize: 12.5 }} color="secondary.main">
                        {t("pickup.selfPickup.remark")}: {packageItem.remark}
                      </Typography>
                    )}

                    {/* actions */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{
                        mt: "auto",
                        pt: 1.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label="Edit package"
                        onClick={() => buttonEditHandler(packageItem)}
                        sx={iconBtnSx}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        sx={{ ml: "auto", fontSize: 11, fontFamily: MONO }}
                        color="text.secondary"
                      >
                        {moment(packageItem.updatedAt)
                          .tz("Asia/Vientiane")
                          .format("DD/MM/YYYY")}
                      </Typography>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
      </Grid>
        </>
      )}

      {/* dialogs */}
      <PackageEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        packageData={packageData}
        rows={rows}
        setRows={setRows}
        onSuccess={reconcileRowsAfterEdit}
        isAgentUser={isAgentUser}
      />
      <TrackingDetailDialog
        open={trackingDialogOpen}
        onClose={() => setTrackingDialogOpen(false)}
        trackingNumber={selectedTrackingNumber}
      />
    </Box>
  );
}

// Cargo Prep (stage 1) tab content: pick the packages going into one box, photograph
// them (2 steps — items, then the sealed box), repeat for the next box. Each section
// below is its own status group so staff always know what's still outstanding, and
// can leave mid-box (after the items photo) and come back later without losing progress.
function CargoPrepPanel({
  t, cr, theme, rows, prepSelected, togglePrepSelected, setPrepSelected, submitItemsPhoto, submitBoxPhoto, prepSubmitting, onTrackingClick,
}) {
  const itemsFileRef = useRef(null);
  const boxFileRef = useRef(null);
  const [openKeys, setOpenKeys] = useState(new Set());

  const groups = { none: [], waitingBoxPhoto: [], complete: [] };
  (rows || []).forEach((p) => groups[cargoPrepStatusOf(p)].push(p));

  // Collapsed by default — auto-open only the section(s) that actually have
  // something in them, so an empty stage doesn't take up space. Staff can still
  // tap a section to peek inside even when it's empty.
  useEffect(() => {
    setOpenKeys(new Set(["none", "waitingBoxPhoto", "complete"].filter((k) => groups[k].length > 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  if (!Array.isArray(rows)) {
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: "16px", border: `1px solid ${theme.palette.divider}` }}>
        <Typography color="text.secondary" sx={{ fontSize: 13 }}>{t("pickup.payment.selectFirst")}</Typography>
      </Paper>
    );
  }

  const isSelected = (pkg) => prepSelected.some((p) => p._id === pkg._id);
  const selectedStatuses = new Set(prepSelected.map((p) => cargoPrepStatusOf(p)));
  // Only allow the action button when every currently-checked package is in the
  // matching status — keeps a box's items/box photo from mixing across groups.
  const canSubmitItems = prepSelected.length > 0 && selectedStatuses.size === 1 && selectedStatuses.has("none");
  const canSubmitBox = prepSelected.length > 0 && selectedStatuses.size === 1 && selectedStatuses.has("waitingBoxPhoto");

  const toggleOpen = (key) =>
    setOpenKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const section = (key, titleKey) => {
    const groupPackages = groups[key];
    const isOpen = openKeys.has(key);
    const allSelected = groupPackages.length > 0 && groupPackages.every((p) => isSelected(p));
    return (
    <Paper elevation={0} sx={{ borderRadius: "14px", border: `1px solid ${theme.palette.divider}`, mb: 1.5, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => toggleOpen(key)}
        sx={{ px: 2, py: 1.5, cursor: "pointer" }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: theme.palette.text.secondary,
              transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform .15s",
            }}
          />
          <Typography sx={{ fontSize: 13, fontWeight: 700 }} color={groupPackages.length > 0 ? "text.primary" : "text.secondary"}>
            {t(`${cr}.prep.${titleKey}`)} · {groupPackages.length}
          </Typography>
        </Stack>
        {key !== "complete" && groupPackages.length > 0 && (
          <Link
            component="button"
            type="button"
            color="secondary"
            underline="hover"
            onClick={(e) => { e.stopPropagation(); setPrepSelected(allSelected ? [] : groupPackages); }}
            sx={{ fontSize: 12, fontWeight: 700 }}
          >
            {allSelected ? t(`${cr}.prep.deselectAll`) : t(`${cr}.prep.selectAll`)}
          </Link>
        )}
      </Stack>
      {isOpen && (
        <Stack spacing={0.25} sx={{ px: 2, pb: 1.5 }}>
          {groupPackages.length === 0 ? (
            <Typography sx={{ fontSize: 12.5 }} color="text.secondary">—</Typography>
          ) : (
            groupPackages.map((p) => (
              <Stack key={p._id} direction="row" alignItems="center" spacing={1} sx={{ py: 0.25 }}>
                {key !== "complete" ? (
                  <Checkbox size="small" color="secondary" checked={isSelected(p)} onChange={() => togglePrepSelected(p)} />
                ) : (
                  <CheckRoundedIcon sx={{ fontSize: 18, color: theme.palette.success.main, ml: 0.75, mr: 0.75 }} />
                )}
                <Link component="button" type="button" color="secondary" underline="hover" onClick={() => onTrackingClick(p.tracking)} sx={{ fontSize: 13, fontWeight: 700 }}>
                  {p.tracking}
                </Link>
                {p.description && <Typography sx={{ fontSize: 12 }} color="text.secondary" noWrap>— {p.description}</Typography>}
              </Stack>
            ))
          )}
        </Stack>
      )}
    </Paper>
    );
  };

  return (
    <Box>
      {section("none", "notStarted")}
      {section("waitingBoxPhoto", "waitingBoxPhoto")}
      {section("complete", "prepped")}

      <Stack direction="row" spacing={1.5}>
        <Button
          variant={canSubmitItems ? "contained" : "outlined"}
          color="secondary"
          startIcon={prepSubmitting ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraOutlinedIcon />}
          disabled={!canSubmitItems || prepSubmitting}
          onClick={() => itemsFileRef.current?.click()}
          sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none" }}
        >
          {t(`${cr}.prep.attachItemsPhoto`)}
        </Button>
        <input
          ref={itemsFileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) submitItemsPhoto(f); }}
        />
        <Button
          variant={canSubmitBox ? "contained" : "outlined"}
          color="secondary"
          startIcon={prepSubmitting ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraOutlinedIcon />}
          disabled={!canSubmitBox || prepSubmitting}
          onClick={() => boxFileRef.current?.click()}
          sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none" }}
        >
          {t(`${cr}.prep.attachBoxPhoto`)}
        </Button>
        <input
          ref={boxFileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) submitBoxPhoto(f); }}
        />
      </Stack>
    </Box>
  );
}

export default CargoReceiptSender;
